import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FileText, Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getStoredTimezone } from '@/lib/timezone-utils';

interface Invoice {
  id: string;
  session_id: string;
  invoice_number: string;
  status: string;
  credit_value_used: number;
  original_hourly_rate: number;
  duration_minutes: number;
  total_session_value: number;
  mentor_payout_amount: number;
  platform_fee_amount: number;
  created_at: string;
  reviewed_at?: string;
  paid_at?: string;
  mentor_notes?: string;
  admin_notes?: string;
  // Session and student details
  sessions?: {
    scheduled_at: string;
    profiles: {
      first_name: string;
      last_name: string;
    };
  };
}


interface MentorInvoiceManagerProps {
  mentorId: string;
}

export function MentorInvoiceManager({ mentorId }: MentorInvoiceManagerProps) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchInvoices();
  }, [mentorId]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('mentor_invoices')
        .select(`
          *,
          sessions!inner(
            scheduled_at,
            profiles!sessions_student_id_fkey(first_name, last_name)
          )
        `)
        .eq('mentor_id', mentorId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInvoices((data || []) as any[]);
    } catch (error: any) {
      console.error('Error fetching invoices:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch invoices',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'disputed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading invoices...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Automation Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 rounded-full bg-blue-500 flex-shrink-0 mt-0.5"></div>
            <div>
              <h3 className="font-semibold text-blue-900">Automatic Invoice Generation</h3>
              <p className="text-blue-800 text-sm">
                Invoices are now automatically generated when your sessions are completed. 
                You can view your payment history and status below.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Existing Invoices */}
      <Card>
        <CardHeader>
          <CardTitle>Your Invoices</CardTitle>
          <CardDescription>
            Track the status of your submitted invoices
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No invoices created yet</p>
              <p className="text-sm text-muted-foreground mt-2">
                Complete some sessions to create invoices
              </p>
            </div>
           ) : (
             <div className="space-y-4">
               {invoices.map((invoice) => (
                 <div key={invoice.id} className="border rounded-lg p-4">
                   <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                     <div className="space-y-2 flex-1 min-w-0">
                       <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                         <span className="font-medium truncate">{invoice.invoice_number}</span>
                         <Badge className={`${getStatusColor(invoice.status)} w-fit`}>
                           {invoice.status}
                         </Badge>
                       </div>
                       <div className="text-sm text-muted-foreground space-y-1">
                         <p className="truncate">Student: {invoice.sessions?.profiles.first_name} {invoice.sessions?.profiles.last_name}</p>
                          <p>Session Date: {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(invoice.sessions?.scheduled_at || ''))}</p>
                          <p>Created: {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(invoice.created_at))}</p>
                       </div>
                     </div>
                     <div className="text-right sm:text-right">
                       <div className="font-bold text-lg">${invoice.mentor_payout_amount.toFixed(2)}</div>
                       <div className="text-sm text-muted-foreground">
                         {invoice.duration_minutes} minutes
                       </div>
                     </div>
                   </div>

                  {/* Invoice Details Dialog */}
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="mt-3">
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Invoice {invoice.invoice_number}</DialogTitle>
                        <DialogDescription>
                          Detailed breakdown of your session invoice
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium mb-2">Session Information</h4>
                            <div className="space-y-1 text-sm">
                              <p><span className="font-medium">Student:</span> {invoice.sessions?.profiles.first_name} {invoice.sessions?.profiles.last_name}</p>
                              <p><span className="font-medium">Date:</span> {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(invoice.sessions?.scheduled_at || ''))}</p>
                              <p><span className="font-medium">Duration:</span> {invoice.duration_minutes} minutes</p>
                            </div>
                          </div>
                          <div>
                            <h4 className="font-medium mb-2">Status Information</h4>
                            <div className="space-y-1 text-sm">
                              <p><span className="font-medium">Status:</span>
                                <Badge className={`ml-2 ${getStatusColor(invoice.status)}`}>
                                  {invoice.status}
                                </Badge>
                              </p>
                              <p><span className="font-medium">Created:</span> {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(invoice.created_at))}</p>
                              {invoice.reviewed_at && (
                                <p><span className="font-medium">Reviewed:</span> {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(invoice.reviewed_at))}</p>
                              )}
                              {invoice.paid_at && (
                                <p><span className="font-medium">Paid:</span> {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(invoice.paid_at))}</p>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="bg-muted/50 p-4 rounded-lg">
                          <h4 className="font-medium mb-3">Financial Breakdown</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span>Credit Value Used:</span>
                              <span>${(Number(invoice.credit_value_used) / 100).toFixed(2)}/credit</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Your Listed Rate:</span>
                              <span>${(Number(invoice.original_hourly_rate) / 100).toFixed(2)}/hour</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Total Session Value:</span>
                              <span>${invoice.total_session_value.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Platform Fee:</span>
                              <span>${invoice.platform_fee_amount.toFixed(2)}</span>
                            </div>
                            <hr className="my-2" />
                            <div className="flex justify-between font-bold">
                              <span>Your Payout:</span>
                              <span>${invoice.mentor_payout_amount.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        {invoice.mentor_notes && (
                          <div>
                            <h4 className="font-medium mb-2">Your Notes</h4>
                            <p className="text-sm bg-muted/50 p-3 rounded">{invoice.mentor_notes}</p>
                          </div>
                        )}

                        {invoice.admin_notes && (
                          <div>
                            <h4 className="font-medium mb-2">Admin Notes</h4>
                            <p className="text-sm bg-blue-50 p-3 rounded border border-blue-200">{invoice.admin_notes}</p>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
